
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';

/** Shared dark surface for the app shell (header + sidebar). */
export const SHELL_BACKGROUND = '#111827';

interface SidebarContainerProps {
  width: string | number;
  /** When true (mobile) the sidebar floats over the content instead of pushing it. */
  overlay?: boolean;
}

export const SidebarContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'overlay',
})<SidebarContainerProps>(({ width, overlay }) => ({
  width,
  flexShrink: 0,
  padding: width ? '1rem' : 0,
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: SHELL_BACKGROUND,
  transition: 'width 0.2s ease, padding 0.2s ease',
  ...(overlay && {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1200,
    boxShadow: width ? '0 12px 32px rgba(0, 0, 0, 0.35)' : 'none',
  }),
}));

/** Dims the content behind the mobile overlay sidebar; click to dismiss. */
export const SidebarBackdrop = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1100,
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
}));
/** A group's row ("CRM", "Settings"): opens and closes the submenu below it. */
export const SectionToggle = styled('button')(() => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  margin: '0.25rem 0 0',
  border: 'none',
  borderRadius: '8px',
  background: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  fontWeight: 500,
  textAlign: 'left',
  color: '#9ca3af',
  padding: '0.65rem 1rem',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    color: '#f9fafb',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  '&:focus-visible': {
    outline: '2px solid #9ca3af',
    outlineOffset: '2px',
  },
  // The group holding the page on screen.
  '&[data-current="true"]': {
    color: '#f9fafb',
  },
  '& > span': {
    marginLeft: '10px',
    marginRight: '8px',
  },
  '& > svg:last-child': {
    marginLeft: 'auto',
    flexShrink: 0,
    transition: 'transform 0.15s ease',
  },
  '&[aria-expanded="true"] > svg:last-child': {
    transform: 'rotate(90deg)',
  },
}));

/** A line down the left of a submenu, so each level reads as inside the one above it. */
export const Branch = styled('div')(() => ({
  marginLeft: '1.5rem',
  paddingLeft: '0.5rem',
  borderLeft: '1px solid rgba(255, 255, 255, 0.14)',
}));

export const commonLink = styled(Link)(() => ({
  fontSize: '0.95rem',
  display: 'grid',
  columnGap: '0.75rem',
  marginTop: '0.25rem',
  padding: '0.65rem 1rem',
  textDecoration: 'none',
  borderRadius: '8px',
  gridTemplateColumns: 'max-content max-content',
  transition: 'background-color 0.15s ease, color 0.15s ease',
}));

export const StyledNavLink = styled(commonLink)`
  color: #9ca3af;
  &:hover {
    color: #f9fafb;
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

export const ActiveLink = styled(commonLink)`
  color: #f9fafb;
  background-color: rgba(255, 255, 255, 0.1);
`

const submenuEntry = {
  display: 'flex',
  alignItems: 'center',
  columnGap: '0.5rem',
  fontSize: '0.875rem',
  marginTop: '0.125rem',
  padding: '0.45rem 0.75rem',
};

/** A page in a submenu. */
export const SubmenuLink = styled(StyledNavLink)(() => submenuEntry);
export const ActiveSubmenuLink = styled(ActiveLink)(() => submenuEntry);

export const LogoContainer = styled('div')`
  font-size: 1.5rem;
  padding: 1rem 0 0.5rem 1.5rem;

  i:hover {
    transition: 0.1s,
    font-size: 2rem,
  }
  i {
    font-size: 2rem,
  }
  img {
    width:60px,
    height:60px,
  }
`;
